import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { EmployeeProfile } from '@/types'

interface ImageGalleryProps {
  employee: EmployeeProfile
}

/**
 * Image Gallery Component - Microsoft Store Style
 * 
 * Displays screenshots in Microsoft Store style with:
 * - Main featured image
 * - Thumbnail navigation bar
 * - Caption labels for each screenshot
 */
export function ImageGallery({ employee }: ImageGalleryProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [selectedImage, setSelectedImage] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const images = employee.images
  const imagesPerSlide = 3
  const totalSlides = Math.ceil(images.length / imagesPerSlide)

  // Sample screenshot labels (Microsoft Store style)
  const screenshotLabels = [
    "Meet Copilot on Windows",
    "Let's get started",
    "AI-powered assistance",
    "Productivity features",
    "Integration examples"
  ]

  const openLightbox = () => {
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => prev === 0 ? totalSlides - 1 : prev - 1)
  }

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setSelectedImage((prev) => prev === 0 ? images.length - 1 : prev - 1)
  }

  const getCurrentSlideImages = () => {
    const startIndex = currentSlide * imagesPerSlide
    return images.slice(startIndex, startIndex + imagesPerSlide)
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowRight':
          if (lightboxOpen) {
            nextImage()
          } else {
            nextSlide()
          }
          break
        case 'ArrowLeft':
          if (lightboxOpen) {
            prevImage()
          } else {
            prevSlide()
          }
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen])

  if (!images || images.length === 0) {
    return null
  }

  return (
    <>
      <div className="space-y-4">
        {/* Three Images Grid */}
        <div className="relative">
          <div className="grid grid-cols-3 gap-3">
            {getCurrentSlideImages().map((image, index) => {
              const globalIndex = currentSlide * imagesPerSlide + index
              return (
                <div
                  key={globalIndex}
                  className="relative aspect-video rounded-lg overflow-hidden bg-muted cursor-pointer border hover:border-border transition-colors"
                  onClick={() => {
                    setSelectedImage(globalIndex)
                    openLightbox()
                  }}
                >
                  <img
                    src={image}
                    alt={screenshotLabels[globalIndex] || `Screenshot ${globalIndex + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                  />
                  {/* Screenshot Label */}
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    {screenshotLabels[globalIndex] || `Screenshot ${globalIndex + 1}`}
                  </div>
                </div>
              )
            })}
          </div>
          
          {/* Navigation arrows for slides */}
          {totalSlides > 1 && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md"
                onClick={prevSlide}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md"
                onClick={nextSlide}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>

        {/* Slide Indicators */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2">
            {Array.from({ length: totalSlides }, (_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentSlide === index 
                    ? 'bg-primary' 
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={closeLightbox}
          >
            <X className="w-6 h-6" />
            <span className="sr-only">Close lightbox</span>
          </Button>

          {/* Previous Button */}
          {images.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={prevImage}
            >
              <ChevronLeft className="w-8 h-8" />
              <span className="sr-only">Previous image</span>
            </Button>
          )}

          {/* Next Button */}
          {images.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={nextImage}
            >
              <ChevronRight className="w-8 h-8" />
              <span className="sr-only">Next image</span>
            </Button>
          )}

          {/* Main Image */}
          <div className="max-w-5xl max-h-full flex items-center justify-center">
            <img
              src={images[selectedImage]}
              alt={screenshotLabels[selectedImage] || `Screenshot ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>

          {/* Image Counter and Label */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
            <div className="bg-black/50 text-white px-3 py-1 rounded-full text-xs mb-2">
              {selectedImage + 1} of {images.length}
            </div>
            <div className="text-white text-xs">
              {screenshotLabels[selectedImage] || `Screenshot ${selectedImage + 1}`}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

ImageGallery.displayName = "ImageGallery"